import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './WalletManagement.css';
import { API_BASE_URL } from '../services/api';

const WalletManagement = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [walletData, setWalletData] = useState(null);
  const [actionType, setActionType] = useState(''); // 'bonus' | 'penalty' | 'redeem'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [familySettings, setFamilySettings] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/parent/login');
      return;
    }

    loadChildren();
    loadFamilySettings();
  }, [navigate]);

  useEffect(() => {
    if (selectedChild) {
      loadWalletData(selectedChild.id);
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/families/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setChildren(data.children || []);
        if (data.children?.length > 0) {
          setSelectedChild(data.children[0]);
        }
      }
    } catch (error) {
      console.error('자녀 목록 로딩 오류:', error);
    }
  };

  const loadFamilySettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/families/me/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFamilySettings(data);
      }
    } catch (error) {
      console.error('가족 설정 로딩 오류:', error);
    }
  };

  const loadWalletData = async (childId, offset = 0) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/wallets/${childId}?limit=5&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (offset === 0) {
          setWalletData(data);
        } else {
          // 더보기 - 기존 거래내역에 추가
          setWalletData(prev => ({
            ...data,
            transactions: [...prev.transactions, ...data.transactions]
          }));
        }
        setHasMore(data.pagination.hasMore);
      } else {
        console.error('지갑 데이터 로딩 실패');
      }
    } catch (error) {
      console.error('지갑 데이터 로딩 오류:', error);
    }
  };

  const loadMoreTransactions = async () => {
    if (!selectedChild || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const currentOffset = walletData?.transactions?.length || 0;
    await loadWalletData(selectedChild.id, currentOffset);
    setLoadingMore(false);
  };

  const handleTransaction = async (e) => {
    e.preventDefault();

    if (!selectedChild || !actionType || !amount || !reason.trim()) {
      setMessage('모든 필드를 입력해주세요.');
      return;
    }

    if (parseInt(amount) <= 0) {
      setMessage('올바른 금액을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let method = 'POST';

      if (actionType === 'bonus') {
        endpoint = `${API_BASE_URL}/wallets/${selectedChild.id}/bonus`;
      } else if (actionType === 'penalty') {
        endpoint = `${API_BASE_URL}/wallets/${selectedChild.id}/penalty`;
      } else if (actionType === 'redeem') {
        endpoint = `${API_BASE_URL}/wallets/${selectedChild.id}/parent-redeem`;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: parseInt(amount),
          reason: reason.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setMessage(`✅ ${result.message}`);
        setAmount('');
        setReason('');
        setActionType('');
        await loadWalletData(selectedChild.id); // 지갑 데이터 새로고침
      } else {
        const error = await response.json();
        throw new Error(error.error || '처리 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('거래 처리 오류:', error);
      setMessage(`❌ ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '0';
    }
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'earn': return '💰';
      case 'bonus': return '🎁';
      case 'penalty': return '⚠️';
      case 'redeem': return '🛒';
      default: return '📊';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'earn':
      case 'bonus':
        return 'positive';
      case 'penalty':
      case 'redeem':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  if (!selectedChild) {
    return <div className="loading">자녀 정보를 불러오는 중...</div>;
  }

  return (
    <div className="wallet-management">
      <div className="wallet-header">
        <button className="back-btn" onClick={() => navigate('/parent/dashboard')}>
          ← 뒤로가기
        </button>
        <h1>도토리 지갑 관리</h1>
      </div>

      {message && (
        <div className={`message ${message.includes('❌') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {/* 자녀 선택 */}
      <div className="child-selector">
        <label>자녀 선택:</label>
        <div className="child-tabs">
          {children.map((child) => (
            <button
              key={child.id}
              className={`child-tab ${selectedChild.id === child.id ? 'active' : ''}`}
              onClick={() => setSelectedChild(child)}
            >
              {child.name}
            </button>
          ))}
        </div>
      </div>

      {/* 지갑 현황 */}
      {walletData && (
        <div className="wallet-overview">
          <h2>💳 {selectedChild.name}의 지갑</h2>
          <div className="balance-card">
            <div className="current-balance">
              <span className="balance-label">현재 잔액</span>
              <span className="balance-amount">{formatCurrency(walletData.wallet?.currentBalance || 0)}원</span>
            </div>
            <div className="balance-stats">
              <div className="stat-item">
                <span>총 획득</span>
                <span className="positive">+{formatCurrency(walletData.wallet?.stats?.totalEarned || 0)}원</span>
              </div>
              <div className="stat-item">
                <span>총 사용</span>
                <span className="negative">-{formatCurrency(walletData.wallet?.stats?.totalSpent || 0)}원</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 도토리 관리 폼 숨김 - 보너스/벌점은 정산 시 자동 처리 */}

      {/* 거래 내역 */}
      {walletData?.transactions && (
        <div className="transaction-history">
          <h3>📋 도토리 상세 현황</h3>
          {walletData.transactions.length === 0 ? (
            <div className="no-transactions">아직 거래 내역이 없습니다.</div>
          ) : (
            <div className="transactions-list">
              {walletData.transactions.map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-left">
                    <span className="transaction-icon">
                      {getTransactionIcon(transaction.type)}
                    </span>
                    <div className="transaction-info">
                      <div className="transaction-desc">{transaction.description}</div>
                      <div className="transaction-date">{formatDate(transaction.createdAt)}</div>
                    </div>
                  </div>
                  <div className="transaction-right">
                    <span className={`transaction-amount ${getTransactionColor(transaction.type)}`}>
                      {transaction.type === 'earn' || transaction.type === 'bonus' ? '+' : '-'}
                      {formatCurrency(transaction.amount)}원
                    </span>
                    <div className="balance-after">
                      잔액: {formatCurrency(transaction.balanceAfter)}원
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 더보기 버튼 */}
          {walletData.transactions.length > 0 && hasMore && (
            <div className="load-more-section">
              <button
                className="load-more-btn"
                onClick={loadMoreTransactions}
                disabled={loadingMore}
              >
                {loadingMore ? '로딩 중...' : '더보기 📄'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WalletManagement;